import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Checklist } from '../../components/Checklist';
import { ORG_DETECT_CHECKLIST } from '../../utils/constants';
import { Eye, AlertTriangle, Zap, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Detect() {
  const breadcrumbs = [{
    label: 'Protocols',
    path: '/'
  }, {
    label: 'Organization Protocol',
    path: '/organization'
  }, {
    label: 'Detection & Monitoring'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-3857">
      <div className="container mx-auto px-4 py-12" data-id="element-3858">
        {/* Header */}
        <div className="mb-12" data-id="element-3859">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-500 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-3860">
            <Eye className="h-3 w-3 mr-2" data-id="element-3861" />
            Domain 4 of 6
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-3862">
            Detection & Monitoring
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-3863">
            Set up logging and monitoring to detect suspicious activity and
            security incidents early, before they cause major damage.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-3864">
          <div className="lg:col-span-2 space-y-12" data-id="element-3865">
            {/* What This Domain Covers */}
            <section data-id="element-3866">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-3867">
                What This Domain Covers
              </h2>
              <ul className="space-y-2 text-slate-300" data-id="element-3868">
                <li className="flex items-start" data-id="element-3869">
                  <span className="text-cyan-500 mr-2" data-id="element-3870">•</span>
                  Security logging and log retention
                </li>
                <li className="flex items-start" data-id="element-3871">
                  <span className="text-cyan-500 mr-2" data-id="element-3872">•</span>
                  Alert configuration for critical events
                </li>
                <li className="flex items-start" data-id="element-3873">
                  <span className="text-cyan-500 mr-2" data-id="element-3874">•</span>
                  Endpoint and email monitoring
                </li>
                <li className="flex items-start" data-id="element-3875">
                  <span className="text-cyan-500 mr-2" data-id="element-3876">•</span>
                  Anomaly detection and triage
                </li>
                <li className="flex items-start" data-id="element-3877">
                  <span className="text-cyan-500 mr-2" data-id="element-3878">•</span>
                  Continuous monitoring processes
                </li>
              </ul>
            </section>

            {/* Minimum Baseline Controls */}
            <section data-id="element-3879">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-3880">
                Minimum Baseline Controls
              </h2>
              <div className="space-y-3" data-id="element-3881">
                <ControlItem title="Enable Logging on Critical Systems" description="Turn on audit logs for admin accounts, email, cloud services, and servers." data-id="element-3882" />
                <ControlItem title="Retain Logs for 90 Days Minimum" description="Keep logs long enough to investigate incidents and meet compliance requirements." data-id="element-3883" />
                <ControlItem title="Monitor Failed Login Attempts" description="Alert on multiple failed logins (potential brute force or credential stuffing)." data-id="element-3884" />
                <ControlItem title="Track Admin Account Usage" description="Log and review all administrative actions and privilege escalations." data-id="element-3885" />
                <ControlItem title="Enable Email Security Alerts" description="Get notified of suspicious emails, phishing attempts, and malware detections." data-id="element-3886" />
                <ControlItem title="Monitor Endpoint Protection Alerts" description="Review antivirus/EDR alerts for malware detections and suspicious behavior." data-id="element-3887" />
              </div>
            </section>

            {/* Best Practice Controls */}
            <section data-id="element-3888">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-3889">
                Best Practice Controls
              </h2>
              <div className="space-y-3" data-id="element-3890">
                <ControlItem title="Centralize Log Collection" description="Aggregate logs from all systems into a central SIEM or log management platform." data-id="element-3891" />
                <ControlItem title="Implement Automated Alerting" description="Configure alerts for high-priority events (account takeover, malware, data exfiltration)." data-id="element-3892" />
                <ControlItem title="Monitor Cloud Service Activity" description="Track configuration changes, data access, and permission modifications in cloud platforms." data-id="element-3893" />
                <ControlItem title="Detect Unusual Login Patterns" description="Alert on logins from new locations, unusual times, or impossible travel scenarios." data-id="element-3894" />
                <ControlItem title="Implement File Integrity Monitoring" description="Detect unauthorized changes to critical system files and configurations." data-id="element-3895" />
                <ControlItem title="Monitor Network Traffic Anomalies" description="Detect unusual data transfers, command-and-control traffic, or lateral movement." data-id="element-3896" />
                <ControlItem title="Establish Triage Process" description="Define how to prioritize and investigate alerts (high/medium/low severity)." data-id="element-3897" />
                <ControlItem title="Conduct Weekly Alert Reviews" description="Review security alerts weekly to identify trends and tune detection rules." data-id="element-3898" />
                <ControlItem title="Test Detection Capabilities" description="Simulate attacks (phishing tests, malware samples) to verify detection works." data-id="element-3899" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-3900">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-3901">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" data-id="element-3902" />
                Common Mistakes
              </h2>
              <div className="space-y-3 text-slate-400" data-id="element-3903">
                <p className="flex items-start" data-id="element-3904">
                  <span className="text-red-500 mr-2" data-id="element-3905">✗</span>
                  Enabling logging but never reviewing logs (logs are useless if
                  no one looks at them)
                </p>
                <p className="flex items-start" data-id="element-3906">
                  <span className="text-red-500 mr-2" data-id="element-3907">✗</span>
                  Alert fatigue from too many false positives (tune alerts or
                  they'll be ignored)
                </p>
                <p className="flex items-start" data-id="element-3908">
                  <span className="text-red-500 mr-2" data-id="element-3909">✗</span>
                  Not retaining logs long enough (many breaches are discovered
                  months later)
                </p>
                <p className="flex items-start" data-id="element-3910">
                  <span className="text-red-500 mr-2" data-id="element-3911">✗</span>
                  Monitoring only perimeter (insider threats and compromised
                  accounts need detection too)
                </p>
                <p className="flex items-start" data-id="element-3912">
                  <span className="text-red-500 mr-2" data-id="element-3913">✗</span>
                  No clear escalation path (alerts mean nothing if no one knows
                  what to do)
                </p>
              </div>
            </section>

            {/* Quick Wins */}
            <section data-id="element-3914">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-3915">
                <Zap className="h-6 w-6 text-yellow-500 mr-3" data-id="element-3916" />
                Quick Wins (7 Days)
              </h2>
              <div className="space-y-3" data-id="element-3917">
                <QuickWin day="Day 1-2" action="Enable audit logging on email and cloud services" data-id="element-3918" />
                <QuickWin day="Day 3-4" action="Set up alerts for failed login attempts (5+ failures in 15 minutes)" data-id="element-3919" />
                <QuickWin day="Day 5-6" action="Configure endpoint protection to send alerts to security owner" data-id="element-3920" />
                <QuickWin day="Day 7" action="Document who receives alerts and how to escalate" data-id="element-3921" />
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-3922">
            <Checklist id="org-detect" title="Implementation Checklist" items={ORG_DETECT_CHECKLIST} data-id="element-3923" />

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-3924">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-3925">
                Priority Signals to Monitor
              </h3>
              <ul className="space-y-2 text-sm text-slate-300" data-id="element-3926">
                <li className="flex items-start" data-id="element-3927">
                  <span className="text-cyan-500 mr-2" data-id="element-3928">•</span>
                  Multiple failed login attempts
                </li>
                <li className="flex items-start" data-id="element-3929">
                  <span className="text-cyan-500 mr-2" data-id="element-3930">•</span>
                  Admin account usage outside business hours
                </li>
                <li className="flex items-start" data-id="element-3931">
                  <span className="text-cyan-500 mr-2" data-id="element-3932">•</span>
                  Malware/ransomware detections
                </li>
                <li className="flex items-start" data-id="element-3933">
                  <span className="text-cyan-500 mr-2" data-id="element-3934">•</span>
                  Unusual data downloads or transfers
                </li>
                <li className="flex items-start" data-id="element-3935">
                  <span className="text-cyan-500 mr-2" data-id="element-3936">•</span>
                  New device or location logins
                </li>
                <li className="flex items-start" data-id="element-3937">
                  <span className="text-cyan-500 mr-2" data-id="element-3938">•</span>
                  Permission changes on critical systems
                </li>
              </ul>
            </Card>

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-3939">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-3940">Next Steps</h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-3941">
                With detection in place, prepare your incident response plan for
                when threats are detected.
              </p>
              <Link to="/organization/respond" data-id="element-3942">
                <Button variant="outline" fullWidth data-id="element-3943">
                  Next: Incident Response
                  <ArrowRight className="h-4 w-4 ml-2" data-id="element-3944" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between items-center border-t border-slate-800 pt-8" data-id="element-3945">
          <Link to="/organization/protect" data-id="element-3946">
            <Button variant="outline" data-id="element-3947">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-3948" />
              Previous: Protection
            </Button>
          </Link>
          <Link to="/organization/respond" data-id="element-3949">
            <Button data-id="element-3950">
              Next Domain
              <ArrowRight className="h-4 w-4 ml-2" data-id="element-3951" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>;
}
function ControlItem({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <Card className="p-4 bg-slate-900/50 border-slate-800" data-id="element-3952">
      <h4 className="font-bold text-slate-100 text-sm mb-1" data-id="element-3953">{title}</h4>
      <p className="text-slate-400 text-sm" data-id="element-3954">{description}</p>
    </Card>;
}
function QuickWin({
  day,
  action
}: {
  day: string;
  action: string;
}) {
  return <div className="flex items-start" data-id="element-3955">
      <span className="text-yellow-500 font-mono text-sm mr-3 mt-0.5" data-id="element-3956">
        {day}
      </span>
      <span className="text-slate-300 text-sm" data-id="element-3957">{action}</span>
    </div>;
}